import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, ArrowLeft, ArrowRight } from 'lucide-react';
import { getPostBySlug, posts } from '../blog/posts';

const SITE_URL = 'https://wrapalotspecialtyfilmz.com';

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const url = `${SITE_URL}/blog/${post.slug}`;
  const otherPosts = posts.filter((p) => p.slug !== post.slug).slice(0, 2);

  const blogPostingLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaDescription,
    image: post.image,
    url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    datePublished: post.publishedDate,
    dateModified: post.updatedDate,
    author: {
      '@type': 'Organization',
      name: post.author,
      url: SITE_URL,
    },
    publisher: { '@id': `${SITE_URL}/#business` },
    keywords: post.keyword,
    articleSection: 'Car Wrap Installation',
    inLanguage: 'en-US',
  };

  const faqLd = post.faqs && post.faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: post.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  } : null;

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
      { '@type': 'ListItem', position: 3, name: post.title, item: url },
    ],
  };

  const displayDate = new Date(post.publishedDate).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <>
      <Helmet>
        <title>{post.title}</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={post.keyword} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={post.image} />
        <meta property="article:published_time" content={post.publishedDate} />
        <meta property="article:modified_time" content={post.updatedDate} />
        <meta property="article:author" content={post.author} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.metaDescription} />
        <meta name="twitter:image" content={post.image} />
        <script type="application/ld+json">{JSON.stringify(blogPostingLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        {faqLd && <script type="application/ld+json">{JSON.stringify(faqLd)}</script>}
      </Helmet>

      {/* Hero */}
      <article className="bg-slate-900" data-testid="blog-post">
        <div className="relative h-[40vh] md:h-[55vh] min-h-[320px] overflow-hidden">
          <img
            src={post.image}
            alt={post.imageAlt}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/60 to-slate-900" />
        </div>

        <div className="max-w-3xl mx-auto px-6 -mt-32 md:-mt-44 relative z-10">
          {/* Breadcrumb */}
          <nav className="text-sm text-gray-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-cyan-400 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-cyan-400 transition-colors">Blog</Link>
          </nav>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            {post.title}
          </h1>

          <div className="flex items-center gap-6 text-sm text-gray-400 mb-10 pb-10 border-b border-slate-800">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {displayDate}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {post.readTime} min read
            </span>
            <span className="text-gray-500">By {post.author}</span>
          </div>

          {/* Article body */}
          <div className="prose-content space-y-5">
            {post.content}
          </div>

          {/* FAQ section */}
          {post.faqs && post.faqs.length > 0 && (
            <div className="mt-16 pt-12 border-t border-slate-800" data-testid="blog-post-faqs">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">
                Frequently asked questions
              </h2>
              <div className="space-y-5">
                {post.faqs.map((faq, idx) => (
                  <details
                    key={idx}
                    className="bg-slate-800/50 rounded-xl p-5 group"
                    data-testid={`blog-faq-${idx}`}
                  >
                    <summary className="cursor-pointer font-semibold text-white text-lg list-none flex justify-between items-center gap-4">
                      {faq.q}
                      <span className="text-cyan-400 transition-transform group-open:rotate-45 text-2xl">+</span>
                    </summary>
                    <p className="mt-3 text-gray-400 leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>
          )}

          {/* Related posts */}
          {otherPosts.length > 0 && (
            <div className="mt-16 pt-12 border-t border-slate-800">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">Keep reading</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {otherPosts.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-related-${p.slug}`}
                    className="group block bg-slate-800/50 rounded-xl overflow-hidden hover:bg-slate-800/70 transition-colors"
                  >
                    <img
                      src={p.image}
                      alt={p.imageAlt}
                      loading="lazy"
                      className="w-full h-32 object-cover"
                    />
                    <div className="p-5">
                      <h3 className="text-white font-semibold mb-2 leading-snug group-hover:text-cyan-400 transition-colors">
                        {p.title}
                      </h3>
                      <span className="text-cyan-400 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Back to blog */}
          <div className="mt-16 pb-20">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
              data-testid="blog-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> Back to all articles
            </Link>
          </div>
        </div>
      </article>
    </>
  );
};

export default BlogPost;
